import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperonLogo } from "@/components/OperonLogo";
import { useTheme } from "@/lib/theme";
import { api, extractErr, setAuthToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sun, Moon, ArrowRight } from "lucide-react";

export default function AcceptInvite() {
  const { theme, toggleTheme } = useTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const token = params.get("token") || "";
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ password: "", full_name: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invitation token"); return; }
    api.get(`/auth/invite/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setMeta(data);
        setForm((p) => ({ ...p, full_name: data.full_name || "" }));
      })
      .catch((e) => setError(extractErr(e, "Invitation not valid")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/invite/accept", { token, password: form.password, full_name: form.full_name });
      setAuthToken(data.access_token);
      await refreshMe();
      toast.success(`Welcome to ${data.company?.name || "Operon"}!`);
      navigate("/app/dashboard");
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  };

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Link to="/" className="flex items-center"><OperonLogo theme={theme} size={32} /></Link>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm space-y-4">
          {error ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
              <div className="font-heading font-semibold text-destructive">Invitation error</div>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
              <Button variant="ghost" asChild className="mt-3"><Link to="/login">Back to login</Link></Button>
            </div>
          ) : !meta ? (
            <div className="rounded-xl border border-border bg-card p-5 text-center text-sm text-muted-foreground">Verifying invitation…</div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
              <div>
                <h1 className="font-heading text-2xl font-semibold tracking-tight">Accept invitation</h1>
                <p className="text-sm text-muted-foreground mt-1">You’re joining <b>{meta.company_name}</b> as <span className="font-mono">{meta.email}</span>.</p>
              </div>
              <div className="space-y-1.5"><Label>Full name</Label><Input required value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} data-testid="accept-invite-name" /></div>
              <div className="space-y-1.5"><Label>Choose a password</Label><Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} data-testid="accept-invite-password" /></div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="accept-invite-submit">{loading ? "Creating account…" : <>Accept &amp; sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
